import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { hasPermission } from '../utils/roles';
import { McpLevelPicker, MCP_LEVELS } from './OAuthAuthorize';

export default function Profile() {
  const { api, user } = useAuth();
  const toast = useToast();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({ email: false, telegram: false, whatsapp: false });
  const [pwForm, setPwForm] = useState({ current: '', newPassword: '', confirm: '' });
  const [payroll, setPayroll] = useState(null);
  const [payrollMonth, setPayrollMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [tgLink, setTgLink] = useState(null);
  const [tgBusy, setTgBusy] = useState(false);

  const load = () => {
    api('/me/notification-settings')
      .then(s => { setSettings(s); setLoading(false); })
      .catch(err => { toast.error(err.message); setLoading(false); });
  };
  useEffect(load, []);

  // المستحقات تُعرض لمن يملك payroll.self فقط — الخادم يردّ 403 لغيره
  useEffect(() => {
    if (!hasPermission(user, 'payroll.self')) return;
    api(`/payroll/mine?month=${payrollMonth}`).then(setPayroll).catch(() => setPayroll(null));
  }, [payrollMonth]);

  const set = (patch) => setSettings(s => ({ ...s, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        email_notifications: settings.email_notifications,
        telegram_notifications: settings.telegram_notifications,
        whatsapp_phone: settings.whatsapp_phone || null,
        whatsapp_notifications: settings.whatsapp_notifications,
      };
      // لا نرسل apikey إذا كان مقنّعاً
      if (settings.whatsapp_apikey && !settings.whatsapp_apikey.startsWith('••')) {
        payload.whatsapp_apikey = settings.whatsapp_apikey;
      }
      await api('/me/notification-settings', { method: 'PUT', body: JSON.stringify(payload) });
      toast.success('تم الحفظ');
      load();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const sendTest = async (channel) => {
    setTesting(t => ({ ...t, [channel]: true }));
    try {
      const r = await api(`/me/notification-settings/test/${channel}`, { method: 'POST' });
      toast.success(r.message || 'تم الإرسال');
    } catch (err) { toast.error(err.message); }
    finally { setTesting(t => ({ ...t, [channel]: false })); }
  };

  // الرمز يُصدر هنا، ويُستهلك حين يضغط الموظّف «ابدأ» في البوت — والخادم
  // يسحب ردّ تليجرام عند سؤال الحالة، فلا webhook على الاستضافة.
  const startTelegramLink = async () => {
    setTgBusy(true);
    try {
      const r = await api('/me/telegram/link', { method: 'POST' });
      setTgLink(r);
      window.open(r.url, '_blank', 'noopener');
    } catch (err) { toast.error(err.message); }
    finally { setTgBusy(false); }
  };

  const checkTelegramLink = async () => {
    setTgBusy(true);
    try {
      const r = await api('/me/telegram/link-status');
      if (r.linked) {
        setTgLink(null);
        set({ telegram_chat_id: r.telegram_chat_id, telegram_notifications: r.telegram_notifications });
        toast.success('تمّ ربط تليجرام');
      } else {
        toast.info('لم يصل الربط بعد. اضغط «ابدأ» في محادثة البوت ثم أعد التحقّق.');
      }
    } catch (err) { toast.error(err.message); }
    finally { setTgBusy(false); }
  };

  const unlinkTelegram = async () => {
    setTgBusy(true);
    try {
      await api('/me/telegram/link', { method: 'DELETE' });
      setTgLink(null);
      set({ telegram_chat_id: null, telegram_notifications: false });
      toast.success('أُلغي ربط تليجرام');
    } catch (err) { toast.error(err.message); }
    finally { setTgBusy(false); }
  };

  const changePassword = async () => {
    if (pwForm.newPassword !== pwForm.confirm) { toast.error('كلمتا المرور غير متطابقتين'); return; }
    if (pwForm.newPassword.length < 6) { toast.error('كلمة المرور قصيرة جداً (أقل من 6 خانات)'); return; }
    try {
      await api('/auth/password', { method: 'PUT', body: JSON.stringify({ current: pwForm.current, newPassword: pwForm.newPassword }) });
      toast.success('تم تغيير كلمة المرور');
      setPwForm({ current: '', newPassword: '', confirm: '' });
    } catch (err) { toast.error(err.message); }
  };

  if (loading || !settings) return <div className="loader">جاري التحميل...</div>;

  return (
    <div style={{ maxWidth: 760 }}>
      <h2 style={{ marginBottom: 20 }}>الملف الشخصي</h2>

      {/* User info */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title" style={{ marginBottom: 12 }}>بياناتي</div>
        <div className="task-meta">
          <div className="meta-item"><div className="meta-label">الاسم</div><div className="meta-value">{user?.name}</div></div>
          <div className="meta-item"><div className="meta-label">البريد</div><div className="meta-value" style={{ direction: 'ltr', textAlign: 'right' }}>{user?.email}</div></div>
          <div className="meta-item"><div className="meta-label">الدور</div><div className="meta-value">{user?.role}</div></div>
        </div>
      </div>

      {payroll && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <div className="card-title" style={{ margin: 0 }}>💰 مستحقاتي</div>
            <input type="month" className="form-input" style={{ width: 160 }} value={payrollMonth} onChange={e => setPayrollMonth(e.target.value)} />
          </div>
          <div className="task-meta" style={{ marginBottom: 12 }}>
            {payroll.statement.salary > 0 && (
              <div className="meta-item"><div className="meta-label">الراتب</div><div className="meta-value">{payroll.statement.salary} {payroll.currency}</div></div>
            )}
            <div className="meta-item"><div className="meta-label">أعمال بالقطعة</div><div className="meta-value">{payroll.statement.piece_total} {payroll.currency}</div></div>
            <div className="meta-item"><div className="meta-label">الإجمالي</div><div className="meta-value" style={{ fontWeight: 700 }}>{payroll.statement.total} {payroll.currency}</div></div>
            <div className="meta-item"><div className="meta-label">غير مُسوّى</div><div className="meta-value">{payroll.statement.due_total} {payroll.currency}</div></div>
          </div>
          {payroll.statement.earnings.length > 0 && (
            <div className="table-wrap">
              <table>
                <thead><tr><th>العمل</th><th>المقدار</th><th>سعر الوحدة</th><th>المبلغ</th><th>الحالة</th></tr></thead>
                <tbody>
                  {payroll.statement.earnings.map(e => (
                    <tr key={e.id}>
                      <td>{e.task_id ? <Link to={`/tasks/${e.task_id}`}>{e.description}</Link> : e.description}</td>
                      <td>{e.quantity}{e.unit ? ` ${e.unit}` : ''}</td>
                      <td>{e.unit_price}</td>
                      <td style={{ fontWeight: 600 }}>{e.amount}</td>
                      <td>
                        <span className={`badge badge-${e.status === 'paid' ? 'completed' : 'pending'}`}>
                          {e.status === 'paid' ? 'مُسوّى' : 'مستحق'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Notification settings */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title" style={{ marginBottom: 4 }}>📬 إشعارات المهام</div>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 0, marginBottom: 16 }}>
          اختر القنوات التي تريد استقبال إشعارات إسناد وتعديل المهام عبرها.
        </p>

        {/* Email */}
        <div style={{ paddingBottom: 16, borderBottom: '1px solid var(--border)', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500, flex: 1, cursor: 'pointer' }}>
              <input type="checkbox" checked={settings.email_notifications} onChange={e => set({ email_notifications: e.target.checked })} />
              📧 البريد الإلكتروني — {settings.email}
            </label>
            <button className="btn btn-outline btn-sm" disabled={!settings.email_notifications || testing.email} onClick={() => sendTest('email')}>
              {testing.email ? '⏳' : 'إرسال اختبار'}
            </button>
          </div>
          {settings.mail_warning && (
            <div style={{ background: 'var(--c-gold-soft)', color: 'var(--c-gold-ink)', padding: 8, borderRadius: 6, fontSize: 12 }}>
              ⚠️ {settings.mail_warning}
            </div>
          )}
        </div>

        {/* Telegram */}
        <div style={{ paddingBottom: 16, borderBottom: '1px solid var(--border)', marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500, marginBottom: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={settings.telegram_notifications} onChange={e => set({ telegram_notifications: e.target.checked })} />
            💬 Telegram
          </label>

          {!settings.has_telegram_bot ? (
            <div style={{ background: 'var(--c-coral-soft)', border: '1px solid var(--c-coral)', color: 'var(--c-danger)', padding: 12, borderRadius: 6, fontSize: 13, lineHeight: 1.7 }}>
              ⚠ المدير لم يضبط بوت Telegram للمنصة بعد. الإشعارات لن تصلك حتى يُضاف <code>TELEGRAM_BOT_TOKEN</code> في إعدادات الخادم.
            </div>
          ) : settings.telegram_chat_id ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, color: 'var(--c-success)' }}>
                ✓ مربوط (محادثة <code>{settings.telegram_chat_id}</code>)
              </span>
              <button className="btn btn-outline btn-sm" disabled={testing.telegram} onClick={() => sendTest('telegram')}>
                {testing.telegram ? '⏳' : 'إرسال اختبار'}
              </button>
              <button className="btn btn-outline btn-sm" disabled={tgBusy} onClick={unlinkTelegram}>
                إلغاء الربط
              </button>
            </div>
          ) : (
            <>
              <div style={{ background: 'var(--c-blue-100)', border: '1px solid var(--c-blue-300)', padding: 12, borderRadius: 6, marginBottom: 12, fontSize: 13, lineHeight: 1.8 }}>
                اضغط «ربط تليجرام» — تُفتح محادثة البوت حاملةً رمز الربط، فاضغط <strong>ابدأ</strong>،
                ثم عُد هنا واضغط «تحقّقت». لا تحتاج نسخ أي رقم.
              </div>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="btn btn-outline btn-sm" disabled={tgBusy} onClick={startTelegramLink}>
                  {tgBusy ? '⏳' : 'ربط تليجرام'}
                </button>
                {tgLink && (
                  <>
                    <button className="btn btn-outline btn-sm" disabled={tgBusy} onClick={checkTelegramLink}>
                      تحقّقت، أكمِل الربط
                    </button>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      إن لم تُفتح المحادثة:{' '}
                      <a href={tgLink.url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>افتحها يدوياً</a>
                      {' '}— الرمز <code>{tgLink.code}</code> صالح ١٥ دقيقة.
                    </span>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* WhatsApp */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500, marginBottom: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={settings.whatsapp_notifications} onChange={e => set({ whatsapp_notifications: e.target.checked })} />
            🟢 WhatsApp (عبر CallMeBot)
          </label>
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">رقم الهاتف (مع رمز الدولة)</label>
              <input className="form-input" placeholder="+966555555555" value={settings.whatsapp_phone || ''} onChange={e => set({ whatsapp_phone: e.target.value })} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">API Key من CallMeBot</label>
              <input className="form-input" placeholder={settings.whatsapp_apikey || 'مفتاح API'} value={settings.whatsapp_apikey?.startsWith('••') ? '' : (settings.whatsapp_apikey || '')} onChange={e => set({ whatsapp_apikey: e.target.value })} />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
            <button className="btn btn-outline btn-sm" disabled={testing.whatsapp} onClick={() => sendTest('whatsapp')}>
              {testing.whatsapp ? '⏳' : 'إرسال اختبار'}
            </button>
          </div>
          <div style={{ background: 'var(--gray-50)', padding: 10, borderRadius: 6, marginTop: 10, fontSize: 12, lineHeight: 1.7, color: 'var(--text-muted)' }}>
            <strong>كيف تحصل على API Key؟</strong>
            <ol style={{ margin: '6px 0 0 18px', padding: 0 }}>
              <li>أضف الرقم <code style={{ direction: 'ltr', display: 'inline-block' }}>+34 644 51 78 26</code> إلى جهات اتصالك</li>
              <li>أرسل له هذه الرسالة بالضبط: <code>I allow callmebot to send me messages</code></li>
              <li>سيرد عليك في غضون دقيقتين بمفتاح API — انسخه والصقه هنا</li>
              <li>قد تحتاج الانتظار 2 دقيقة بين كل رسالة (حد المجاني)</li>
            </ol>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? '...' : 'حفظ التفضيلات'}</button>
        </div>
      </div>

      {hasPermission(user, 'mcp.connect') && <McpTokens />}

      {/* Change password */}
      <div className="card">
        <div className="card-title" style={{ marginBottom: 12 }}>🔒 تغيير كلمة المرور</div>
        <div className="form-group">
          <label className="form-label">كلمة المرور الحالية</label>
          <input className="form-input" type="password" value={pwForm.current} onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} />
        </div>
        <div className="form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">كلمة المرور الجديدة</label>
            <input className="form-input" type="password" value={pwForm.newPassword} onChange={e => setPwForm(f => ({ ...f, newPassword: e.target.value }))} />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">تأكيد كلمة المرور</label>
            <input className="form-input" type="password" value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={changePassword} disabled={!pwForm.current || !pwForm.newPassword}>تغيير</button>
        </div>
      </div>
    </div>
  );
}

// الرمز يُعرض مرّة واحدة عند إصداره — الخادم لا يحفظ إلا بصمته
function McpTokens() {
  const { api } = useAuth();
  const toast = useToast();
  const [tokens, setTokens] = useState([]);
  const [form, setForm] = useState({ name: '', level: 'read', days: 90 });
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(0);

  const load = () => api('/me/mcp-tokens').then(setTokens).catch(err => toast.error(err.message));
  useEffect(() => {
    load();
    api('/me/mcp-approvals/count').then(r => setPending(r.count)).catch(() => {});
  }, []);

  const issue = async () => {
    setBusy(true);
    try {
      const r = await api('/me/mcp-tokens', { method: 'POST', body: JSON.stringify(form) });
      setIssued(r);
      setForm({ name: '', level: 'read', days: 90 });
      load();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm('إلغاء الرمز يوقف كل موصل يستخدمه فوراً. متابعة؟')) return;
    try {
      await api(`/me/mcp-tokens/${id}`, { method: 'DELETE' });
      toast.success('أُلغي الرمز');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const dismissIssued = () => {
    if (window.confirm('لن يظهر الرمز مرّة أخرى. هل نسخته؟')) setIssued(null);
  };

  const command = issued
    ? `claude mcp add --scope user --transport http ops-platform ${issued.endpoint} --header "Authorization: Bearer ${issued.token}"`
    : '';

  const copy = (text) => navigator.clipboard?.writeText(text).then(() => toast.success('نُسخ'));
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('ar') : '—';

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-title" style={{ marginBottom: 4 }}>🤖 موصل الذكاء الاصطناعي (MCP)</div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 0, marginBottom: 16, lineHeight: 1.7 }}>
        يربط Claude بالمنصّة بصلاحياتك أنت. تختار عند الربط مستواه؛ في «التنفيذ المستقل» توافق مرّة واحدة،
        ولا يصلك إلا ما تشترط سياسة الجمعية موافقتك عليه بعينه، تُشعَر به على قنواتك وتوافق من صفحة «موافقات الموصل».
      </p>
      <div style={{ background: 'var(--c-blue-100)', border: '1px solid var(--c-blue-300)', padding: 12, borderRadius: 6, marginBottom: 16, fontSize: 13, lineHeight: 1.8 }}>
        <strong>من claude.ai أو تطبيق Claude:</strong> الإعدادات ← الموصلات ← إضافة موصل مخصّص، والصق الرابط
        {' '}<code style={{ direction: 'ltr', display: 'inline-block' }}>{`${window.location.origin}/api/mcp`}</code>{' '}
        ثم «اتصال» — تُفتح صفحة موافقة هنا ولا حاجة لرمز. أمّا Claude Code فبالرمز أدناه.
      </div>

      <Link to="/approvals" className="btn btn-outline btn-sm" style={{ marginBottom: 16 }}>
        موافقات الموصل{pending > 0 ? ` — ${pending} معلّقة` : ''}
      </Link>

      {issued && (
        <div style={{ background: 'var(--c-gold-soft)', color: 'var(--c-gold-ink)', padding: 12, borderRadius: 6, margin: '16px 0', fontSize: 13, lineHeight: 1.8 }}>
          <strong>انسخ الرمز الآن — لن يظهر مرّة أخرى.</strong> صالح حتى {fmtDate(issued.expires_at)}.
          <div style={{ direction: 'ltr', textAlign: 'left', marginTop: 8 }}>
            <code style={{ wordBreak: 'break-all' }}>{issued.token}</code>
          </div>
          <div style={{ marginTop: 8 }}>الصق هذا الأمر في الطرفية (يعمل في Claude Code من أيّ مجلد)، ثم اكتب <code>/mcp</code> داخل Claude للتحقّق:</div>
          <pre style={{ direction: 'ltr', textAlign: 'left', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: '4px 0', fontSize: 12 }}>{command}</pre>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-outline btn-sm" onClick={() => copy(command)}>نسخ الأمر</button>
            <button className="btn btn-outline btn-sm" onClick={() => copy(issued.token)}>نسخ الرمز وحده</button>
            <button className="btn btn-outline btn-sm" onClick={dismissIssued}>تمّ</button>
          </div>
        </div>
      )}

      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 16 }}>
        <div className="form-group">
          <label className="form-label" htmlFor="mcp-token-name">اسم رمز جديد</label>
          <input id="mcp-token-name" className="form-input" placeholder="مثل: Claude على جهازي" maxLength={60} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        </div>
        <McpLevelPicker name="mcp-level" value={form.level} onChange={level => setForm(f => ({ ...f, level }))} />
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" htmlFor="mcp-token-days">الصلاحية</label>
            <select id="mcp-token-days" className="form-input" value={form.days} onChange={e => setForm(f => ({ ...f, days: Number(e.target.value) }))}>
              <option value={30}>٣٠ يوماً</option>
              <option value={90}>٩٠ يوماً</option>
              <option value={365}>سنة</option>
            </select>
          </div>
          <button className="btn btn-primary" disabled={busy || !form.name.trim()} onClick={issue}>
            {busy ? '...' : 'إصدار رمز'}
          </button>
        </div>
      </div>

      {tokens.length > 0 && (
        <div className="table-wrap" style={{ marginTop: 16 }}>
          <table>
            <thead><tr><th>الاسم</th><th>النطاق</th><th>آخر استخدام</th><th>ينتهي</th><th></th></tr></thead>
            <tbody>
              {tokens.map(t => (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td>{MCP_LEVELS[t.level]}</td>
                  <td>{t.last_used_at ? new Date(t.last_used_at).toLocaleString('ar') : 'لم يُستخدم'}</td>
                  <td>{fmtDate(t.expires_at)}</td>
                  <td><button className="btn btn-outline btn-sm" aria-label={`إلغاء الرمز ${t.name}`} onClick={() => revoke(t.id)}>إلغاء</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
